import { useEffect, useState } from "react";
import PageHeader from "./PageHeader";
import { useSiteContent } from "../../context/SiteContext";
import { toast } from "sonner";
import { Radio } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "https://effortless-metaphor-api.ogtommype.workers.dev";

const BOOT_LINES = [
  "> initiating handshake...",
  "> auth: anonymous_listener",
  "> loading // TRANSMISSIONS_UNKNOWN.dat",
  "> bypassing firewall // OK",
  "> scanning frequencies...",
  "> READY. awaiting signal.",
];

export default function TheUnknown() {
  const content = useSiteContent();
  const [shown, setShown] = useState([]);
  const [form, setForm] = useState({ handle: "", message: "" });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const transmissions = content.transmissions || [];

  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setShown(BOOT_LINES.slice(0, i));
      if (i >= BOOT_LINES.length) clearInterval(id);
    }, 380);
    return () => clearInterval(id);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) {
      toast.error("Empty signal");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/transmissions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Signal transmitted.");
        setForm({ handle: "", message: "" });
        setSubmitted(true);
      } else {
        toast.error(data.error || "Transmission failed");
      }
    } catch {
      toast.error("Transmission failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-the-unknown" className="relative">
      {/* Background */}
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 50% 50%, rgba(0, 255, 106, 0.1) 0%, transparent 70%), linear-gradient(45deg, #0A0A0A 25%, transparent 25%, transparent 75%, #0A0A0A 75%, #0A0A0A), linear-gradient(45deg, #0A0A0A 25%, transparent 25%, transparent 75%, #0A0A0A 75%, #0A0A0A)",
          backgroundSize: "100% 100%, 20px 20px, 20px 20px",
          backgroundPosition: "center, 0 0, 10px 10px",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/40 via-[#0A0A0A]/80 to-[#0A0A0A] pointer-events-none" />

      <div className="relative">
        <PageHeader
          code="05"
          label="UNKNOWN SIGNALS"
          title="Unknown Signals"
          subtitle="Transmissions from the void. Origin signals from Effortless Metaphor. Community transmissions from those who found the frequency."
        />

        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Transmission Log */}
          <div className="em-card p-5 font-mono text-sm" data-testid="terminal">
            <div className="flex items-center justify-between border-b border-[#27272A] pb-2 mb-3">
              <span className="label-mono text-neon">// TRANSMISSION_LOG</span>
              <span className="label-mono text-neutral-500">/freq/unknown</span>
            </div>
            <div className="space-y-1 min-h-[140px]">
              {shown.map((l, i) => (
                <div key={i} className={i === shown.length - 1 ? "text-neon" : "text-neutral-300"}>
                  {l}
                </div>
              ))}
              {shown.length < BOOT_LINES.length && (
                <div className="text-neon caret">&nbsp;</div>
              )}
            </div>

            {transmissions.length > 0 && (
              <div className="border-t border-[#27272A] mt-6 pt-4 space-y-5">
                {transmissions.map((t) => (
                  <article key={t.id} data-testid={`transmission-${t.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="label-mono text-neon">{t.handle}</span>
                      <span className="label-mono text-neutral-600">{t.createdAt?.split("T")[0]}</span>
                    </div>
                    {t.title && (
                      <h3 className="font-display font-bold text-lg mt-1 text-neon">
                        {t.title}
                      </h3>
                    )}
                    <p className="text-neutral-300 mt-1 leading-relaxed">{t.message}</p>
                  </article>
                ))}
              </div>
            )}

            {transmissions.length === 0 && shown.length >= BOOT_LINES.length && (
              <div className="border-t border-[#27272A] mt-6 pt-4 text-neutral-500">
                // silence on all frequencies
              </div>
            )}
          </div>

          {/* Submission + Info */}
          <div>
            <div className="em-card p-6">
              <div className="flex items-center gap-2">
                <Radio size={16} className="text-neon" />
                <div className="label-mono text-neutral-500">// broadcast</div>
              </div>
              <h3 className="font-display font-bold text-2xl mt-2">
                Transmission Broadcast
              </h3>
              <p className="text-neutral-400 text-sm mt-2">
                It's worth a shot.
              </p>

              {submitted ? (
                <div className="mt-6 border border-neon/30 p-4">
                  <div className="text-neon font-mono text-sm">// SIGNAL SENT</div>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 flex flex-col gap-4" data-testid="transmission-form">
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="label-mono text-neutral-500">Callsign</label>
                      <span className="label-mono text-neutral-600">{form.handle.length}/16</span>
                    </div>
                    <input
                      data-testid="transmission-handle"
                      value={form.handle}
                      onChange={(e) => setForm({ ...form, handle: e.target.value.slice(0, 16) })}
                      placeholder="ANONYMOUS"
                      maxLength={16}
                      className="mt-2 w-full bg-[#0A0A0A] border border-[#27272A] focus:border-neon outline-none px-3 py-2 font-mono text-sm uppercase tracking-wider"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="label-mono text-neutral-500">Signal</label>
                      <span className={`label-mono ${form.message.length > 120 ? "text-yellow-500" : "text-neutral-600"}`}>
                        {form.message.length}/140
                      </span>
                    </div>
                    <textarea
                      data-testid="transmission-message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value.slice(0, 140) })}
                      rows={3}
                      maxLength={140}
                      placeholder="> compress your thought..."
                      className="mt-2 w-full bg-[#0A0A0A] border border-[#27272A] focus:border-neon outline-none px-3 py-2 font-mono text-sm resize-none"
                    />
                  </div>
                  <button
                    data-testid="transmission-submit"
                    disabled={sending || !form.message.trim()}
                    className="btn-primary self-start disabled:opacity-40"
                    type="submit"
                  >
                    {sending ? "TRANSMITTING..." : "BROADCAST"}
                  </button>
                </form>
              )}
            </div>

            <div className="em-card p-6 mt-5">
              <div className="label-mono text-neutral-500">// fragments</div>
              <ul className="mt-3 space-y-2 text-sm text-neutral-300 font-mono">
                <li>&gt; if the page is empty, you are early</li>
                <li>&gt; this page may rewrite itself</li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
